import { useState } from 'react'
import type { ConversationMessage } from '../../api/agent'
import type { Tag } from '../../api/tags'
import { getNote } from '../../api/notes'

interface ContextPillsProps {
  mode: 'smart_search' | 'manual'
  tags: Tag[]
  pinnedNoteCount: number
  onOpenContextBag?: () => void
}

export default function ContextPills({ mode, tags, pinnedNoteCount, onOpenContextBag }: ContextPillsProps) {
  if (mode === 'smart_search' && tags.length === 0 && pinnedNoteCount === 0) return null

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 px-4 py-2 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={onOpenContextBag}
      title="Click to open Context Bag"
    >
      {/* Mode pill */}
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          mode === 'manual'
            ? 'bg-amber-100 text-amber-800'
            : 'bg-blue-100 text-blue-800'
        }`}
      >
        {mode === 'manual' ? (
          <>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Manual
          </>
        ) : (
          <>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Smart Search
          </>
        )}
      </span>

      {/* Tag pills */}
      {tags.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs font-medium"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
          </svg>
          {tag.name}
        </span>
      ))}

      {/* Pinned notes count */}
      {pinnedNoteCount > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {pinnedNoteCount} note{pinnedNoteCount !== 1 ? 's' : ''} attached
        </span>
      )}
    </div>
  )
}

// Standalone message sources display
export function MessageSources({
  message,
}: {
  message: ConversationMessage
}) {
  const [resolvedNotes, setResolvedNotes] = useState<Array<{ id: string; name: string } | null> | null>(null)
  const [loading, setLoading] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const hasAnything =
    (message.search_queries?.length ?? 0) > 0 ||
    (message.sources_consulted?.length ?? 0) > 0

  if (!hasAnything && !message.notes_found) return null

  const PREVIEW_COUNT = 5
  const sourceIds = message.sources_consulted ?? []

  async function handleOpen(e: React.SyntheticEvent<HTMLDetailsElement>) {
    const el = e.currentTarget
    // Only resolve on first open
    if (el.open && resolvedNotes === null && sourceIds.length > 0) {
      setLoading(true)
      const results = await Promise.all(
        sourceIds.map((id) =>
          getNote(id)
            .then((n) => ({ id: n.id, name: n.name }))
            .catch(() => ({ id, name: id })) // fallback to raw ID if fetch fails
        )
      )
      setResolvedNotes(results)
      setLoading(false)
    }
  }

  const notesToShow = resolvedNotes
    ? (showAll ? resolvedNotes : resolvedNotes.slice(0, PREVIEW_COUNT))
    : []

  const summaryLabel = `Sources (${message.notes_found} note${message.notes_found !== 1 ? 's' : ''}${
    message.external_sources_used > 0 ? `, ${message.external_sources_used} external` : ''
  })`

  return (
    <details
      className="mt-2 text-xs text-gray-500 group"
      onToggle={handleOpen}
    >
      <summary className="cursor-pointer select-none hover:text-gray-700 flex items-center gap-1 list-none">
        <svg className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {summaryLabel}
      </summary>

      <div className="mt-1.5 pl-4 space-y-2">
        {/* Search queries */}
        {(message.search_queries?.length ?? 0) > 0 && (
          <div>
            <span className="font-medium text-gray-600">Search queries:</span>{' '}
            <span className="text-gray-500">{message.search_queries!.join(' · ')}</span>
          </div>
        )}

        {/* Source notes */}
        {sourceIds.length > 0 && (
          <div>
            <span className="font-medium text-gray-600">Notes consulted:</span>
            {loading ? (
              <span className="ml-1 text-gray-400">Resolving…</span>
            ) : resolvedNotes ? (
              <ul className="mt-1 space-y-0.5">
                {notesToShow.map((note) =>
                  note ? (
                    <li key={note.id}>
                      <a
                        href={`/notes/${note.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline hover:text-secondary"
                      >
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {note.name}
                      </a>
                    </li>
                  ) : null
                )}
                {!showAll && resolvedNotes.length > PREVIEW_COUNT && (
                  <li>
                    <button
                      onClick={() => setShowAll(true)}
                      className="text-primary hover:underline hover:text-secondary"
                    >
                      + {resolvedNotes.length - PREVIEW_COUNT} more note{resolvedNotes.length - PREVIEW_COUNT !== 1 ? 's' : ''}
                    </button>
                  </li>
                )}
                {showAll && resolvedNotes.length > PREVIEW_COUNT && (
                  <li>
                    <button
                      onClick={() => setShowAll(false)}
                      className="text-gray-400 hover:text-gray-600 hover:underline"
                    >
                      Show fewer
                    </button>
                  </li>
                )}
              </ul>
            ) : null}
          </div>
        )}

        {/* Token usage */}
        {message.total_tokens > 0 && (
          <div>
            <span className="font-medium text-gray-600">Tokens used:</span>{' '}
            {message.total_tokens.toLocaleString()}
          </div>
        )}
      </div>
    </details>
  )
}

